type RecordObject = Record<string, unknown>;

export type PipelineMockRelation = {
  sourceComponent: string;
  relation_type: string;
  targetComponent: string;
};

function asRecord(value: unknown): RecordObject | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as RecordObject)
    : null;
}

function readString(source: RecordObject, keys: string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim() !== '') {
      return value.trim();
    }
  }

  return '';
}

function readLogId(log: unknown): string {
  const record = asRecord(log);
  if (!record) {
    return '';
  }

  const logId = record.logId ?? record.log_id;
  return typeof logId === 'string' ? logId : '';
}

function readLogContext(log: unknown): RecordObject {
  const record = asRecord(log) ?? {};
  return asRecord(record.context) ?? record;
}

/** Mirrors JetStream buildMockComponentGraphs for pipeline debug sandbox. */
export function buildPipelineMockRelations(
  logs: unknown[],
  anomalyEvents: unknown[],
): PipelineMockRelation[] {
  const relations = new Map<string, PipelineMockRelation>();
  const logContextById = new Map<string, RecordObject>();

  for (const log of logs) {
    const logId = readLogId(log);
    const context = readLogContext(log);

    if (logId) {
      logContextById.set(logId, context);
    }

    const componentName = readString(context, [
      'componentName',
      'component_name',
      'component_display_name',
    ]);
    const assetId = readString(context, ['tenantAssetId', 'tenant_asset_id']);
    const siteId = readString(context, ['siteId', 'site_id']);

    if (componentName && assetId) {
      const relation: PipelineMockRelation = {
        sourceComponent: componentName,
        relation_type: 'part_of_asset',
        targetComponent: assetId,
      };
      relations.set(JSON.stringify(relation), relation);
    }

    if (assetId && siteId) {
      const relation: PipelineMockRelation = {
        sourceComponent: assetId,
        relation_type: 'located_at',
        targetComponent: siteId,
      };
      relations.set(JSON.stringify(relation), relation);
    }
  }

  for (const anomalyEvent of anomalyEvents) {
    const record = asRecord(anomalyEvent) ?? {};
    const relatedLogContext = logContextById.get(
      readString(record, ['log_id', 'logId']),
    );
    const componentName =
      readString(record, ['component_name', 'componentName']) ||
      readString(relatedLogContext ?? {}, [
        'componentName',
        'component_name',
        'component_display_name',
      ]);
    const assetId =
      readString(record, ['tenant_asset_id', 'tenantAssetId']) ||
      readString(relatedLogContext ?? {}, ['tenantAssetId', 'tenant_asset_id']);

    if (componentName && assetId) {
      const relation: PipelineMockRelation = {
        sourceComponent: componentName,
        relation_type: 'connected_to',
        targetComponent: assetId,
      };
      relations.set(JSON.stringify(relation), relation);
    }
  }

  return [...relations.values()];
}
